import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  login as apiLogin,
  register as apiRegister,
  logout as apiLogout,
  getCurrentUser,
} from '../api/auth';
import { API_URL, AUTH_TOKEN_KEY } from '../api/config';
import { refresh } from '../api/refresh';

const AuthContext = createContext(null);

// Сеть/5xx на старте (auth-service перезапускается) — не повод выкидывать на /login:
// повторяем, пока сервис не поднимется. 401/403 — ответ по существу, его не повторяем.
const RETRY_DELAYS_MS = [1000, 2000, 3000, 5000];
const isTransient = (error) =>
  Boolean(error?.isAxiosError) && (!error.response || error.response.status >= 500);

const withTransientRetry = async (fn, isCancelled) => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (!isTransient(error) || attempt >= RETRY_DELAYS_MS.length || isCancelled()) throw error;
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
    }
  }
};

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      try {
        // Единый вход между приложениями: access-токен лежит в localStorage этого origin,
        // а refresh-кука общая для всех приложений. Нет токена — пробуем войти по куке,
        // иначе покажем форму входа человеку, который уже вошёл в другом приложении.
        if (!localStorage.getItem(AUTH_TOKEN_KEY)) {
          try {
            await withTransientRetry(() => refresh(), () => cancelled);
          } catch {
            // живой сессии нет — форма входа
          }
        }
        if (cancelled || !localStorage.getItem(AUTH_TOKEN_KEY)) return;

        const userData = await withTransientRetry(getCurrentUser, () => cancelled);
        if (!cancelled && userData?.user) {
          setUser(userData.user);
          setIsAuthenticated(true);
        }
      } catch (error) {
        // Interceptor already tried refresh on 401. If it still failed with 401,
        // it emits 'auth:session-expired' — the listener below handles cleanup.
        if (!cancelled && error?.response?.status !== 401) {
          console.warn('Auth check failed after retries', error?.message);
        }
      } finally {
        if (!cancelled) setIsInitializing(false);
      }
    };

    checkAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onSessionExpired = () => {
      setUser(null);
      setIsAuthenticated(false);
      if (window.location.pathname !== '/login') {
        navigate('/login');
        toast.info('Session expired, please log in again');
      }
    };
    window.addEventListener('auth:session-expired', onSessionExpired);
    return () => window.removeEventListener('auth:session-expired', onSessionExpired);
  }, [navigate]);

  const login = async (email, password) => {
    try {
      setIsAuthenticating(true);
      const response = await apiLogin(email, password);

      if (!response?.tokens?.accessToken) {
        throw new Error('No access token received');
      }
      localStorage.setItem(AUTH_TOKEN_KEY, response.tokens.accessToken);

      if (!response?.user) {
        throw new Error('No user data received');
      }
      setUser(response.user);
      setIsAuthenticated(true);
      navigate('/');
      toast.success('Successfully logged in');
      return true;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  };

  const register = async (fullName, email, password, passwordConfirm) => {
    try {
      setIsAuthenticating(true);
      const response = await apiRegister({ fullName, email, password, passwordConfirm });

      if (response?.success) {
        toast.success(
          response.message ||
            'Registration successful! Please wait for administrator approval.',
          { autoClose: 10000 }
        );
        return true;
      }
      throw new Error('Registration failed');
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  };

  const logout = async () => {
    try {
      setIsAuthenticating(true);
      await apiLogout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      setUser(null);
      setIsAuthenticated(false);
      setIsAuthenticating(false);
      navigate('/login');
      toast.info('You have been logged out');
    }
  };

  const hasRole = (role) => {
    if (!user || !user.roles || user.roles.length === 0) return false;
    return user.roles.some((r) => (typeof r === 'string' ? r === role : r.name === role));
  };

  const isAdmin = () => hasRole('super-admin');

  const isUserActive = () => user && user.isActive !== false;

  const setTokensAndFetchUser = async (accessToken) => {
    localStorage.setItem(AUTH_TOKEN_KEY, accessToken);
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      isLoading: isInitializing,
      isAuthenticating,
      login,
      register,
      logout,
      hasRole,
      isAdmin,
      isUserActive,
      setTokensAndFetchUser,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, isAuthenticated, isInitializing, isAuthenticating]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
